import { BarChart3, Clock, AlertTriangle, AlertCircle } from 'lucide-react'

import { WORKFLOW_STAGE_LABELS, WORKFLOW_STAGE_COLORS } from '@/components/pm/gantt/types'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import {
  useStageStatistics,
  formatHours,
  type AnalyticsFilters,
} from '@/hooks'
import { cn } from '@/lib/utils'

interface StageStatisticsChartProps {
  filters?: AnalyticsFilters
  className?: string
}

/**
 * 阶段统计图表组件
 * Requirements: 3.1, 3.3, 3.8
 */
export function StageStatisticsChart({ filters = {}, className }: StageStatisticsChartProps) {
  const { data: statistics, isLoading, error } = useStageStatistics(filters)

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            阶段耗时统计
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Clock className="h-5 w-5 animate-spin mr-2" />
            加载中...
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            阶段耗时统计
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-destructive">
            <AlertCircle className="h-5 w-5 mr-2" />
            加载失败
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!statistics || statistics.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            阶段耗时统计
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            暂无数据
          </div>
        </CardContent>
      </Card>
    )
  }

  // 找出最大耗时用于计算进度条比例
  const maxDuration = Math.max(...statistics.map((s) => s.averageDurationHours), 1)

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          阶段耗时统计
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {statistics.map((stat) => (
            <div key={stat.stage} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      'w-3 h-3 rounded-full',
                      WORKFLOW_STAGE_COLORS[stat.stage]
                    )}
                  />
                  <span className="text-sm font-medium">
                    {WORKFLOW_STAGE_LABELS[stat.stage]}
                  </span>
                  {stat.isBottleneck && (
                    <Badge variant="destructive" className="text-xs">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      瓶颈
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-muted-foreground">
                    预估: {formatHours(stat.estimatedDurationHours)}
                  </span>
                  <span className="font-medium">
                    实际: {formatHours(stat.averageDurationHours)}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Progress
                  value={(stat.averageDurationHours / maxDuration) * 100}
                  className={cn(
                    'h-2 flex-1',
                    stat.isBottleneck && '[&>div]:bg-red-500'
                  )}
                />
                <span className="text-xs text-muted-foreground w-16 text-right">
                  {stat.completedTasks}/{stat.totalTasks} 任务
                </span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
